import { getRoot, IModelType, types } from 'mobx-state-tree';
import { createThunk, model, Model, view } from 'mst-collection';

import {
  Api,
  ICreateSubscriptionPaymentIntentRequestParams,
  ICreateCreditsPaymentIntentRequestParams,
  IPushNotificationsTokenRequest,
  IShortLinkRequestParams,
  IUpdateProfileBodyRequest,
  IValidateCouponCodeRequestParams,
  IConfirmPaymentIntentRequestParams,
  schemas,
} from '@api';
import { AnalyticsService, LocalizationService } from '@services';
import { getChangedData, trimData } from '@utils';
import { safeReference } from '../types';
import { useRoot } from '../useRoot';
import { ICurrentUser, IUser, UserModel } from './UserModel';
import type { RootModelType } from '../RootModel';
import { UserWatchProgressModel } from './UserWatchProgressModel';
import { UsersSuggestionModel } from './UsersSuggestionModel';
import { TariffPlansModel } from './TariffPlansModel';
import { PaymentMethodListModel } from './PaymentMethodListModel';
import { SearchedUserListModel } from './SearchedUserListModel';

export class Viewer extends Model({
  currentUser: types.maybe(
    safeReference<IModelType<any, ICurrentUser>>(UserModel as any),
  ),
  // TODO: Create Users model and add searched and suggested users
  usersSearch: types.optional(SearchedUserListModel, {}),
  usersSuggestion: types.optional(UsersSuggestionModel, {}),
  userWatchProgress: types.optional(UserWatchProgressModel, {}),
  tariffPlans: types.optional(TariffPlansModel, {}),
  paymentMethodList: types.optional(PaymentMethodListModel, {}),
}) {
  // TODO: move it into UserCollectionModel
  @view
  getUserById(id: number): IUser | undefined {
    return getRoot<RootModelType>(this).entities.users.get(id);
  }

  setUser(user: ICurrentUser) {
    this.currentUser = user;
  }

  syncLanguage() {
    if (this.currentUser) {
      const userLanguage = this.currentUser.settings.language;

      if (LocalizationService.language !== userLanguage) {
        LocalizationService.changeLanguage(userLanguage);
      }
    }
  }

  fetchUserById = createThunk((id: number) => {
    return async function fetchById(this: Viewer, flow) {
      const res = await Api.Profile.getSharedDataById({ id });

      flow.merge(res.data, schemas.UserSchema);
    };
  });

  fetch = createThunk(() => {
    return async function fetch(this: Viewer, flow) {
      const res = await Api.Profile.viewer();

      const { result } = flow.merge(res.data, schemas.UserSchema);

      this.setUser(result);
      this.syncLanguage();
      AnalyticsService.setUserId(res.data.id);
    };
  });

  /**
   * Extra ThunkModel for UI purposes
   */
  refresh = createThunk(() => {
    return async function refresh(this: Viewer) {
      await this.fetch.run();
    };
  });

  update = createThunk((params: IUpdateProfileBodyRequest) => {
    return async function update(this: Viewer, flow) {
      const data = trimData(params);
      const currentData = { ...this.currentUser, photo: undefined };
      const changedData = getChangedData(currentData, data);

      const res = await Api.Profile.updateProfile(changedData);

      if (res.data?.token) {
        await getRoot<RootModelType>(this).auth.updateTokens(
          res.data,
        );
      }

      const newUserData = {
        ...this.currentUser,
        ...changedData,
        settings: {
          ...this.currentUser?.settings,
          ...changedData?.settings,
        },
      };

      flow.merge(newUserData, schemas.UserSchema);
    };
  });

  uploadPhoto = createThunk((params: FormData) => {
    return async function uploadPhoto(this: Viewer, flow) {
      const res = await Api.Profile.updatePhoto(params);

      const newUserData = {
        ...this.currentUser,
        photo: res.data,
      };

      flow.merge(newUserData, schemas.UserSchema);
    };
  });

  removePhoto = createThunk(() => {
    return async function removePhoto(this: Viewer, flow) {
      await Api.Profile.removePhoto();

      const newUserData = {
        ...this.currentUser,
        photo: {},
      };

      flow.merge(newUserData, schemas.UserSchema);
    };
  });

  addPushNotificationsToken = createThunk(
    (params: IPushNotificationsTokenRequest) => {
      return async function addPushNotificationsToken() {
        await Api.Notifications.addPushNotificationsToken(params);
      };
    },
  );

  removePushNotificationsToken = createThunk(
    (params: IPushNotificationsTokenRequest) => {
      return async function removePushNotificationsToken() {
        await Api.Notifications.removePushNotificationsToken(params);
      };
    },
  );

  createShortLink = createThunk((params: IShortLinkRequestParams) => {
    return async function createShortLink() {
      const res = await Api.Linking.shortLink(params);

      return res.data;
    };
  });

  createSubscriptionPaymentIntent = createThunk(
    (params: ICreateSubscriptionPaymentIntentRequestParams) => {
      return async function createSubscriptionPaymentIntent() {
        const res =
          await Api.Payments.createSubscriptionPaymentIntent(params);

        return res.data;
      };
    },
  );

  createCreditsPaymentIntent = createThunk(
    (params: ICreateCreditsPaymentIntentRequestParams) => {
      return async function createCreditsPaymentIntent() {
        const res = await Api.Payments.createCreditsPaymentIntent(
          params,
        );

        return res.data;
      };
    },
  );

  confirmPayment = createThunk(
    (params: IConfirmPaymentIntentRequestParams) => {
      return async function confirmPayment() {
        await Api.Payments.confirmPayment(params);
      };
    },
  );

  confirmSubscriptionPayment = createThunk(
    (params: IConfirmPaymentIntentRequestParams) => {
      return async function confirmSubscriptionPayment() {
        const res = await Api.Auth.signUpConfirm({
          ...params,
        });

        // AnalyticsService.trackUserParams({
        //   signUpDate,
        // });

        return res.data;
      };
    },
  );

  validateCouponCode = createThunk(
    (params: IValidateCouponCodeRequestParams) => {
      return async function validateCouponCode() {
        const res = await Api.Payments.validateCouponCode(params);

        return res.data;
      };
    },
  );
}

export const ViewerModel = model(Viewer);

export function useViewerModel() {
  const root = useRoot();

  return root.viewer;
}
